import React, { useState } from 'react';
import { Card, CardHeader, Heading, CardBody, useColorModeValue, Box, Text } from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import { RotateCw } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { getAgingAnalysis } from '../../../../redux/actions/dashboard';

const AgingBarChart = ({ data, title = 'Aging Analysis', height = 350, financialAnalysisDateRange, user }) => {
   // Color mode values must be declared at the top
   const textColor = useColorModeValue('gray.800', 'white');
   const chartBg = useColorModeValue('white', 'gray.700');
   const gridColor = useColorModeValue('#E2E8F0', '#4A5568');
   const tooltipBg = useColorModeValue('#FFFFFF', '#2D3748');
   const tooltipTextColor = useColorModeValue('#1A202C', '#F7FAFC');
   const barColor = useColorModeValue('#3182CE', '#63B3ED');
   const dataLabelColor = useColorModeValue('#FFFFFF', '#1A202C');
   const isDark = useColorModeValue(false, true);
   const [isRotating, setIsRotating] = useState(false);
   const dispatch = useDispatch();

   if (!data || data.length === 0) {
      return (
         <Card h={height} bg={chartBg}>
            <CardHeader>
               <Heading size="md" color={textColor}>
                  {title}
               </Heading>
            </CardHeader>
            <CardBody>
               <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Text color={textColor}>No data available</Text>
               </Box>
            </CardBody>
         </Card>
      );
   }

   const categories = data.map((d) => d.aging_bucket);
   const seriesData = data.map((d) => d.total_outstanding);
   const tooltipInfo = data.map((d) => ({
      avg: d.avg_outstanding,
      count: d.transaction_count,
      percent: d.percentage_of_total,
   }));

   const options = {
      chart: {
         type: 'bar',
         toolbar: { show: false },
         background: chartBg,
         foreColor: textColor,
      },
      plotOptions: {
         bar: {
            horizontal: false,
            columnWidth: '50%',
            borderRadius: 6,
         },
      },
      dataLabels: {
         enabled: true,
         formatter: (val) => '₹' + val.toLocaleString('en-IN', { maximumFractionDigits: 2 }),
         style: {
            colors: [dataLabelColor],
         },
      },
      xaxis: {
         categories,
         title: {
            text: 'Aging Bucket',
            style: {
               color: textColor,
            },
         },
         labels: {
            style: {
               colors: textColor,
            },
         },
         axisBorder: {
            color: gridColor,
         },
         axisTicks: {
            color: gridColor,
         },
      },
      yaxis: {
         title: {
            text: 'Total Outstanding (₹)',
            style: {
               color: textColor,
            },
         },
         labels: {
            formatter: (val) => '₹' + val.toLocaleString('en-IN'),
            style: {
               colors: textColor,
            },
         },
      },
      tooltip: {
         custom: ({ dataPointIndex }) => {
            const d = tooltipInfo[dataPointIndex];
            return `
          <div style="padding:8px; background:${tooltipBg}; color:${tooltipTextColor}; border-radius:4px">
            <strong>Transactions:</strong> ${d.count}<br/>
            <strong>Avg Outstanding:</strong> ₹${Number(d.avg).toLocaleString('en-IN')}<br/>
            <strong>Percent of Total:</strong> ${d.percent}%
          </div>
        `;
         },
      },
      fill: {
         colors: [barColor],
      },
      grid: {
         borderColor: gridColor,
         strokeDashArray: 4,
         xaxis: {
            lines: {
               show: false,
            },
         },
         yaxis: {
            lines: {
               show: true,
            },
         },
      },
   };

   const series = [
      {
         name: 'Total Outstanding',
         data: seriesData,
      },
   ];

   const handleReload = async () => {
      setIsRotating(true);
      dispatch(
         getAgingAnalysis({
            companyId: user?.loginhistory?.companyId,
            fromDate: financialAnalysisDateRange.fromDate,
            toDate: financialAnalysisDateRange.toDate,
         }),
      );
      setTimeout(() => setIsRotating(false), 700);
   };

   return (
      <Card h={height} bg={chartBg}>
         <CardHeader display="flex">
            <Heading size="md" color={textColor}>
               {title}
            </Heading>
            <button
               onClick={handleReload}
               className={`ml-auto p-2 rounded-full transition-all hover:scale-110 ${
                  isDark ? 'hover:bg-gray-600 text-blue-400' : 'hover:bg-gray-100 text-blue-600'
               }`}>
               <RotateCw
                  className={`w-5 h-5 transform ${
                     isRotating ? 'rotate-[360deg]' : ''
                  } transition-transform ease-in-out`}
                  style={{
                     transitionDuration: isRotating ? '700ms' : '10ms',
                  }}
               />
            </button>
         </CardHeader>
         <CardBody>
            <ReactApexChart options={options} series={series} type="bar" height={height - 80} />
         </CardBody>
      </Card>
   );
};

export default AgingBarChart;
